import {useEffect, useRef, useState} from 'react';
import {ACCOUNT_ID} from '../../api/account';
import {getTale, getVoices, startTale, subscribeTale, unlockTale} from '../../api/skazki';
import {trackEvent} from '../../utils/analytics';
import {showError} from '../../utils/notify';
import {skazkiAudio} from '../../utils/skazkiAudio';
import {
    ageDescription,
    ageWord,
    normalizeChildName,
    SKAZKI_INTERESTS,
    SKAZKI_MORALS,
} from '../../config/skazkiConstants';
import SkazkiNightPlayer from './SkazkiNightPlayer';
import SkazkiUnlockSheet from './SkazkiUnlockSheet';

// Positional pastel tints for the voice cards (cycles if more voices arrive).
const VOICE_TINTS = ['#EAF1FF', '#FFEAEA', '#EAF7EE', '#FFF6E0', '#F1EAFF'];

const STORAGE_KEY = 'selebrus_skazki_draft';
const TALE_ID_STORAGE_KEY = 'selebrus_skazki_tale_id';
// The lesson sent to the backend when the user doesn't pick one — the /v1 commit
// requires a non-empty `teaches`.
const DEFAULT_TEACHES = 'доброте';
// Voice's default selection.
const DEFAULT_VOICE_ID = 'uncle';
// Selectable child ages, 3–16 (kept in sync with the backend's age bounds).
const SKAZKI_AGES = Array.from({length: 14}, (_, i) => i + 3);

function loadDraft() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function saveDraft(d) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(d));
    } catch {
    }
}

function clearDraft() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
    }
}

function loadSavedTaleId() {
    try {
        return localStorage.getItem(TALE_ID_STORAGE_KEY) || null;
    } catch {
        return null;
    }
}

function saveTaleId(id) {
    try {
        if (id) localStorage.setItem(TALE_ID_STORAGE_KEY, id);
    } catch {
    }
}

function clearSavedTaleId() {
    try {
        localStorage.removeItem(TALE_ID_STORAGE_KEY);
    } catch {
    }
}

export default function SkazkiCard({
                                       initialAction,
                                       initialTaleId,
                                       initialChildName,
                                       initialAge,
                                   }) {
    const draft = useRef(loadDraft()).current;

    // Single-page form by default; an external tale id (or a saved unpaid preview)
    // opens the player/paywall directly without flashing the form first.
    const [step, setStep] = useState(() => {
        if (initialTaleId) return 'player';
        if (initialAction === 'new') return 'form';
        if (loadSavedTaleId()) return 'player';
        return 'form';
    });

    const [childName, setChildName] = useState(initialChildName || draft?.childName || '');
    const [age, setAge] = useState(initialAge || draft?.age || 5);
    const [interests, setInterests] = useState(draft?.interests || []); // array of labels
    const [customInterest, setCustomInterest] = useState(draft?.customInterest || '');
    const [showCustomInterest, setShowCustomInterest] = useState(!!draft?.customInterest);
    const [voiceId, setVoiceId] = useState(draft?.voiceId || null);
    // Language is fixed to Russian — no user-facing toggle.
    const languageId = 'ru';
    // The lesson is a single free-text field; preset chips just fill it.
    // Fall back to the legacy `moral` draft key so old drafts don't lose it.
    const [customMoral, setCustomMoral] = useState(draft?.customMoral || draft?.moral || '');

    const [taleId, setTaleId] = useState(initialTaleId || null);
    const [currentTale, setCurrentTale] = useState(null);
    const [voices, setVoices] = useState([]);
    // Voice-sample preview uses its own Audio instance so it never touches the
    // main tale playback singleton (skazkiAudio).
    const voicePreviewRef = useRef(null);
    const [previewVoiceId, setPreviewVoiceId] = useState(null);
    const [nameError, setNameError] = useState(null);
    const [generationError, setGenerationError] = useState(null);
    const [moderationError, setModerationError] = useState({interest: null, moral: null});
    const [unlocking, setUnlocking] = useState(false);
    const [unlockSheetOpen, setUnlockSheetOpen] = useState(false);
    const pollAbortRef = useRef(null);
    // Monotonic token used to ignore results from a superseded/cancelled
    // generate request (e.g. the user hit "Отменить" mid-generation).
    const streamReqRef = useRef(0);
    // Focus target when the user submits with an empty name.
    const nameInputRef = useRef(null);

    const normalizedName = normalizeChildName(childName) || '';

    // Persist the form draft so a reload keeps the entered fields.
    useEffect(() => {
        if (step !== 'form') return;
        saveDraft({
            childName, age, interests, customInterest,
            voiceId, languageId, customMoral,
        });
    }, [step, childName, age, interests, customInterest, voiceId, languageId, customMoral]);

    // Load the voice catalog from the tales service; re-fetch on language change
    // so the display copy localises. DEFAULT_VOICE_ID is floated to the front so
    // it lands as the pre-selected (first) card.
    useEffect(() => {
        const orderVoices = (list) => {
            const idx = list.findIndex(v => v.id === DEFAULT_VOICE_ID);
            if (idx <= 0) return list;
            const copy = list.slice();
            const [first] = copy.splice(idx, 1);
            return [first, ...copy];
        };
        getVoices(languageId)
            .then(list => setVoices(orderVoices(list)))
            .catch(() => {
                setVoices(orderVoices([
                    {id: 'friend', name: 'Подружка', caption: 'Лёгкий, дружелюбный', sample_url: ''},
                    {id: 'aunt', name: 'Тётя', caption: 'Тёплый, заботливый', sample_url: ''},
                    {id: 'uncle', name: 'Дядя', caption: 'Уверенный, бархатистый', sample_url: ''},
                ]));
            });
    }, [languageId]);

    // Stop any voice-sample preview when the component unmounts.
    useEffect(() => () => {
        try {
            voicePreviewRef.current?.pause();
        } catch { /* ignore */
        }
    }, []);

    // Play/stop a voice's sample clip. Selecting the voice is handled separately
    // (clicking the card), so this only toggles the preview audio.
    const toggleVoicePreview = (voice) => {
        if (!voicePreviewRef.current) {
            voicePreviewRef.current = new Audio();
            voicePreviewRef.current.addEventListener('ended', () => setPreviewVoiceId(null));
            voicePreviewRef.current.addEventListener('error', () => setPreviewVoiceId(null));
        }
        const a = voicePreviewRef.current;
        if (previewVoiceId === voice.id) {
            a.pause();
            setPreviewVoiceId(null);
            return;
        }
        if (!voice.sample_url) return;
        a.src = voice.sample_url;
        a.currentTime = 0;
        a.play().then(() => setPreviewVoiceId(voice.id)).catch(() => setPreviewVoiceId(null));
    };

    useEffect(() => {
        if (voiceId || !voices.length) return;
        const preferred = voices.find(v => v.id === DEFAULT_VOICE_ID) || voices[0];
        if (preferred) setVoiceId(preferred.id);
    }, [voices, voiceId]);

    // After leaving the form we swap to the player layout — re-anchor to the
    // top of the card so the user isn't left mid-scroll.
    useEffect(() => {
        if (step !== 'player') return;
        const el = document.getElementById('sk-skazki-card-root');
        if (!el) return;
        try {
            el.scrollIntoView({behavior: 'smooth', block: 'start'});
        } catch {
            el.scrollIntoView();
        }
    }, [step]);

    // Load an externally-provided / restored tale into the player.
    useEffect(() => {
        if (step !== 'player' || !taleId) return;
        if (currentTale && currentTale.id === taleId) return; // already loaded (e.g. just created)
        let cancelled = false;
        getTale(taleId).then(t => {
            if (cancelled) return;
            setCurrentTale(t);
            if (t.audio_url) skazkiAudio.loadTale(t);
        }).catch(() => {
            if (!cancelled) setGenerationError('load');
        });
        return () => {
            cancelled = true;
        };
    }, [step, taleId, currentTale]);

    // Restore an in-progress / finished tale on mount (page refresh).
    useEffect(() => {
        if (initialTaleId || initialAction === 'new') return;
        const savedId = loadSavedTaleId();
        if (!savedId) return;
        let cancelled = false;
        getTale(savedId).then(t => {
            if (cancelled) return;
            if (t.status === 'ready' || t.status === 'processing') {
                // The night player doubles as the progress screen — a
                // still-generating tale opens it with skeleton slots.
                setTaleId(savedId);
                setCurrentTale(t);
                if (t.audio_url) skazkiAudio.loadTale(t);
                setStep('player');
            } else {
                clearSavedTaleId();
                setStep('form');
            }
        }).catch(() => {
            if (cancelled) return;
            clearSavedTaleId();
            setStep('form');
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Watch the committed tale via the SSE event stream while it's generating.
    // Every snapshot lands in currentTale, so the player reveals assets
    // (title, cover, text, audio) one by one as they become ready.
    useEffect(() => {
        const shouldWatch = step === 'player' && taleId
            && currentTale && currentTale.status === 'processing';
        if (!shouldWatch) return undefined;

        let disposed = false;
        const unsubscribe = subscribeTale(taleId, {
            onSnapshot: (t) => {
                if (disposed) return;
                setCurrentTale(t);
                if (t.status === 'ready' && t.audio_url) {
                    skazkiAudio.loadTale(t);
                    clearDraft();
                    // A preview keeps its saved id so a reload reopens the
                    // locked player; a paid tale is finished — forget it.
                    if (t.paid) clearSavedTaleId();
                } else if (t.status === 'failed') {
                    setGenerationError('failed');
                }
            },
            onError: () => {
                if (!disposed) setGenerationError('failed');
            },
        });

        // Safety net: if no terminal event arrives, surface a timeout rather than
        // spinning forever (mirrors the old bounded poll ≈ 5 min).
        const timeout = setTimeout(() => {
            if (disposed || finished) return;
            setGenerationError('failed');
            showError('Озвучка занимает дольше обычного. Обновите страницу или попробуйте позже.');
        }, 5 * 60 * 1000);

        pollAbortRef.current = () => {
            disposed = true;
            clearTimeout(timeout);
            unsubscribe();
        };
        return () => {
            disposed = true;
            clearTimeout(timeout);
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, taleId, currentTale?.status]);

    // ---------- Actions ----------
    const toggleInterest = (label) => {
        setInterests(prev => {
            const arr = prev.map(x => typeof x === 'string' ? x : x.label);
            if (arr.includes(label)) return arr.filter(x => x !== label);
            if (arr.length >= 5) return arr;
            return [...arr, label];
        });
    };
    const isInterestOn = (label) => interests.map(x => typeof x === 'string' ? x : x.label).includes(label);
    const interestCount = interests.length;

    const resetForm = () => {
        streamReqRef.current++; // ignore any in-flight generate result
        pollAbortRef.current?.(); // stop the SSE watch
        clearDraft();
        clearSavedTaleId();
        setTaleId(null);
        setCurrentTale(null);
        setUnlocking(false);
        setUnlockSheetOpen(false);
        setChildName('');
        setAge(5);
        setInterests([]);
        setCustomInterest('');
        setShowCustomInterest(false);
        setVoiceId(null);
        setCustomMoral('');
        setNameError(null);
        setGenerationError(null);
        setStep('form');
    };

    // The single "Создать сказку" action: create a draft, set its params, and
    // commit it for full generation (`startTale` = create → patch → commit). The
    // generating screen stays up while the poll effect waits for the audio.
    const createTale = async () => {
        const clean = normalizeChildName(childName);
        if (!clean) {
            setNameError('Без имени сказка будет не такой волшебной');
            nameInputRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
            nameInputRef.current?.focus({preventScroll: true});
            return;
        }
        const custom = customInterest.trim();
        if (custom && custom.length < 2) {
            setModerationError(e => ({...e, interest: 'Давайте выберем что-то другое'}));
            return;
        }
        const customMoralText = customMoral.trim();
        if (customMoralText && customMoralText.length < 2) {
            setModerationError(e => ({...e, moral: 'Давайте выберем что-то другое'}));
            return;
        }
        const allInterests = interests.map(i => (typeof i === 'string' ? i : i.label));
        if (custom) allInterests.push(custom);
        // The /v1 commit requires a non-empty interests list.
        if (allInterests.length === 0) {
            setModerationError(e => ({...e, interest: 'Выберите хотя бы один интерес'}));
            return;
        }
        setChildName(clean);
        setNameError(null);
        trackEvent('skazki_create_clicked');
        try {
            voicePreviewRef.current?.pause();
        } catch { /* ignore */
        }
        setPreviewVoiceId(null);

        const reqId = ++streamReqRef.current;
        // The /v1 commit requires a `teaches`; default it when none was entered.
        const teaches = customMoral.trim() || DEFAULT_TEACHES;

        setGenerationError(null);
        setTaleId(null);
        setCurrentTale({
            id: null,
            status: 'processing',
            title: `Сказка для ${clean}`,
            has_title: false,
            text: '',
            chapters: null,
            audio_url: null,
            alignment_url: null,
            cover_url: null,
            duration_sec: null,
            child_name: clean,
            age,
            interests: allInterests,
            voice_id: voiceId,
            paid: true,
        });
        setStep('player');

        let tale;
        try {
            tale = await startTale({
                accountId: ACCOUNT_ID,
                childName: clean,
                age,
                interests: allInterests,
                teaches,
                voiceId,
                language: languageId,
            });
        } catch {
            if (streamReqRef.current !== reqId) return;
            setGenerationError(navigator.onLine === false ? 'network' : 'failed');
            return;
        }
        if (streamReqRef.current !== reqId) return;
        if (!tale?.id) {
            setGenerationError('failed');
            return;
        }

        setTaleId(tale.id);
        saveTaleId(tale.id);
        // Merge the committed snapshot over the placeholder; the SSE watch then
        // streams asset updates into the player as they finish.
        setCurrentTale(prev => ({...(prev || {}), ...tale}));
    };

    // Swap the (stopped) preview for the freshly unlocked tale: it goes back to
    // 'processing', which re-arms the SSE watch and the player's skeletons.
    const applyUnlockedTale = (t) => {
        skazkiAudio.stopAndClear();
        setGenerationError(null);
        setCurrentTale(t);
        if (t?.id) saveTaleId(t.id);
    };

    const handleUnlock = async () => {
        if (!taleId || unlocking) return;
        trackEvent('skazki_unlock_clicked');
        setUnlocking(true);
        try {
            const t = await unlockTale(taleId);
            applyUnlockedTale(t);
        } catch (e) {
            if (e?.status === 402) setUnlockSheetOpen(true);
            else showError('Не получилось открыть сказку. Попробуйте ещё раз.');
        } finally {
            setUnlocking(false);
        }
    };

    // After a successful payment the credit may land with a webhook delay —
    // retry the unlock a few times before giving up.
    const handlePaid = async () => {
        setUnlockSheetOpen(false);
        setUnlocking(true);
        try {
            for (let attempt = 0; ; attempt++) {
                try {
                    const t = await unlockTale(taleId);
                    applyUnlockedTale(t);
                    return;
                } catch (e) {
                    if (e?.status !== 402 || attempt >= 4) throw e;
                    await new Promise(r => setTimeout(r, 1500));
                }
            }
        } catch {
            showError('Оплата прошла, но открыть сказку не удалось. Обновите страницу — всё откроется.');
        } finally {
            setUnlocking(false);
        }
    };

    // ============ PLAYER (night screen: progress + preview + full player) ====
    if (step === 'player') {
        if (!currentTale) {
            return (
                <div className="max-w-[720px] mx-auto px-4 py-16 text-center">
                    {generationError ? (
                        <>
                            <p className="text-[#3D3D3D]/80">Не удалось открыть сказку.</p>
                            <button onClick={() => window.location.reload()}
                                    className="sk-btn sk-btn--ghost h-10 px-4 text-sm mt-4">
                                Обновить страницу
                            </button>
                        </>
                    ) : (
                        <>
                            <div
                                className="inline-block w-10 h-10 border-2 border-gray-200 border-t-[#FF6464] rounded-full animate-spin"/>
                            <p className="text-[#3D3D3D]/80 mt-4">Загружаем сказку…</p>
                        </>
                    )}
                </div>
            );
        }
        return (
            <div id="sk-skazki-card-root">
                <SkazkiNightPlayer
                    tale={currentTale}
                    voiceName={voices.find(v => v.id === currentTale.voice_id)?.name || null}
                    error={generationError}
                    unlocking={unlocking}
                    onUnlock={handleUnlock}
                    onRetry={normalizedName ? createTale : () => window.location.reload()}
                    onBack={resetForm}
                    onCreateAnother={resetForm}
                />
                {unlockSheetOpen && (
                    <SkazkiUnlockSheet
                        onClose={() => setUnlockSheetOpen(false)}
                        onPaid={handlePaid}
                    />
                )}
            </div>
        );
    }

    // ============ FORM (single page) ============
    const selectedLabels = interests.map(i => (typeof i === 'string' ? i : i.label));
    const customI = customInterest.trim();
    const previewInterests = [...selectedLabels, ...(customI ? [customI] : [])];
    const moralText = customMoral.trim().toLowerCase();
    const atMax = interestCount >= 5;

    return (
        <Shell>
            <div className="md:grid md:grid-cols-[1fr_minmax(320px,380px)]">
                {/* ── Left: voice · lesson · interests ── */}
                <div className="px-5 pt-6 pb-8 md:px-10 md:py-10">
                    {/* Voice picker */}
                    <h2 className="sk2-section-title">Выберите голос</h2>
                    <div className="mt-4 flex flex-wrap gap-3">
                        {voices.map((v, i) => {
                            const on = voiceId === v.id;
                            const playing = previewVoiceId === v.id;
                            const caption = v.caption || v.description || '';
                            return (
                                <button
                                    key={v.id} type="button" onClick={() => setVoiceId(v.id)}
                                    aria-pressed={on} aria-label={`Голос ${v.name}`}
                                    className={`sk2-voice-card flex-1 min-w-[140px] max-w-[210px] ${on ? 'sk2-voice-card--on' : ''}`}
                                    style={{background: VOICE_TINTS[i % VOICE_TINTS.length]}}>
                                    <span
                                        role="button" tabIndex={-1}
                                        aria-label={playing ? `Остановить ${v.name}` : `Послушать ${v.name}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setVoiceId(v.id);
                                            toggleVoicePreview(v);
                                        }}
                                        className="sk2-voice-play">
                                        {playing ? <PauseIcon/> : <PlayIcon/>}
                                    </span>
                                    <div
                                        className="mt-3 font-bold text-[15px] text-[#221d2d] leading-tight">{v.name}</div>
                                    {caption &&
                                        <div className="text-[12px] text-[#6b6b76] leading-snug mt-1">{caption}</div>}
                                    {on && <span className="sk2-voice-check"><CheckIcon/></span>}
                                </button>
                            );
                        })}
                    </div>

                    {/* Lesson / moral — free-text field is primary; chips just fill it */}
                    <h2 className="sk2-section-title mt-9">Чему учит сказка</h2>
                    <input
                        type="text" value={customMoral}
                        onChange={(e) => {
                            setCustomMoral(e.target.value.slice(0, 80));
                            setModerationError(s => ({...s, moral: null}));
                        }}
                        aria-label="Чему учит сказка"
                        placeholder="Например: быть добрым к животным, не бояться темноты…"
                        className={`sk2-input mt-4 ${moderationError.moral ? 'sk2-input--error' : ''}`}/>
                    {moderationError.moral &&
                        <p className="mt-2 text-xs text-error">{moderationError.moral}</p>}
                    <div className="mt-3 flex flex-wrap items-center gap-2.5">
                        <span className="text-[12px] text-[#8a8a95] mr-0.5">или выберите:</span>
                        {SKAZKI_MORALS.map(m => {
                            const on = customMoral.trim().toLowerCase() === m.toLowerCase();
                            return (
                                <button key={m} type="button"
                                        onClick={() => {
                                            setCustomMoral(on ? '' : m);
                                            setModerationError(s => ({...s, moral: null}));
                                        }}
                                        aria-pressed={on}
                                        className={`sk2-chip ${on ? 'sk2-chip--on' : ''}`}>{m.toLowerCase()}</button>
                            );
                        })}
                    </div>

                    {/* Interests */}
                    <h2 className="sk2-section-title mt-9">Интересы</h2>
                    <div className="mt-4 flex flex-wrap gap-2.5">
                        {SKAZKI_INTERESTS.map(({emoji, label}) => {
                            const on = isInterestOn(label);
                            const blocked = !on && atMax;
                            return (
                                <button key={label} type="button" onClick={() => toggleInterest(label)}
                                        aria-pressed={on} disabled={blocked}
                                        className={`sk2-chip ${on ? 'sk2-chip--on' : ''} ${blocked ? 'opacity-40 cursor-not-allowed' : ''}`}>
                                    <span className="text-base">{emoji}</span><span>{label}</span>
                                </button>
                            );
                        })}
                        {showCustomInterest ? (
                            <input
                                autoFocus type="text" value={customInterest}
                                onChange={(e) => {
                                    const v = e.target.value.replace(/\p{Emoji_Presentation}/gu, '').slice(0, 50);
                                    setCustomInterest(v);
                                    setModerationError(s => ({...s, interest: null}));
                                }}
                                onBlur={() => setShowCustomInterest(false)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        setShowCustomInterest(false);
                                    }
                                }}
                                placeholder="например, бабушкин сад"
                                className="h-[38px] px-4 rounded-full border-[1.5px] border-dashed border-[#c9c9d1] bg-white text-[14px] text-[#221d2d] placeholder:text-[#b9b9c2] outline-none focus:border-[#FF7A5C] min-w-[180px]"/>
                        ) : customI ? (
                            <span className="sk2-chip sk2-chip--on">
                                <button type="button" onClick={() => setShowCustomInterest(true)}
                                        className="leading-none">{customI}</button>
                                <button type="button"
                                        onClick={() => {
                                            setCustomInterest('');
                                            setShowCustomInterest(false);
                                        }}
                                        aria-label="Убрать" className="ml-0.5 text-[15px] leading-none">×</button>
                            </span>
                        ) : (
                            <button type="button" onClick={() => setShowCustomInterest(true)}
                                    className="sk2-chip border-dashed text-[#9A9AA2]">+ своё
                            </button>
                        )}
                    </div>
                    {moderationError.interest &&
                        <p className="mt-2 text-xs text-error">{moderationError.interest}</p>}
                </div>

                {/* ── Right: name · age · summary · generate ── */}
                <div
                    className="border-t md:border-t-0 md:border-l border-[#EFEFF3] px-5 py-7 md:px-8 md:py-10 md:sticky md:top-4 md:self-start">
                    <label htmlFor="sk-child-name" className="sk2-label">Имя ребёнка</label>
                    <input
                        ref={nameInputRef}
                        id="sk-child-name" type="text" value={childName} autoCapitalize="words"
                        autoComplete="off" maxLength={24}
                        onChange={e => {
                            setChildName(e.target.value);
                            setNameError(null);
                        }}
                        className={`sk2-input mt-2 ${nameError ? 'sk2-input--error' : ''}`}
                        aria-invalid={nameError ? 'true' : undefined}
                        aria-label="Имя ребёнка" placeholder="Имя ребёнка"/>
                    {nameError && <p className="mt-2 text-sm text-error">{nameError}</p>}

                    <div className="sk2-label mt-6">Возраст</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {SKAZKI_AGES.map(a => {
                            const on = age === a;
                            return (
                                <button key={a} type="button" onClick={() => setAge(a)} aria-pressed={on}
                                        aria-label={`Возраст ${a}`}
                                        className={`sk2-age ${on ? 'sk2-age--on' : ''}`}>{a}</button>
                            );
                        })}
                    </div>
                    <div className="text-[12px] text-[#8a8a95] mt-2.5">{ageWord(age)} · {ageDescription(age)}.</div>

                    <div className="sk2-eyebrow mt-7 mb-2">Сводка сказки</div>
                    <div className="sk2-summary">
                        Сказка для <Hi>{normalizedName || '…'}</Hi>, <Hi>{ageWord(age)}</Hi>
                        {previewInterests.length > 0 && <>, про <Hi>{previewInterests.join(', ')}</Hi></>}
                        {moralText && <> — которая учит <Hi>{moralText}</Hi></>}.
                    </div>

                    <button type="button" onClick={createTale} disabled={!normalizedName}
                            className="sk2-generate mt-6 disabled:opacity-50 disabled:cursor-not-allowed">
                        <SparkleIcon/> Сгенерировать сказку
                    </button>
                    <div className="text-center text-[11px] text-[#9A9AA2] mt-3">
                        ≈ готово за 30 секунд
                    </div>
                </div>
            </div>
        </Shell>
    );
}

// ==================== Layout helpers ====================

function Shell({children}) {
    return (
        <div
            id="sk-skazki-card-root"
            className="max-w-[1100px] mx-auto my-2 md:my-8 bg-white md:rounded-[24px] md:border md:border-[#F0F0F4] md:shadow-[0_12px_44px_rgba(20,20,43,0.08)] overflow-hidden">
            {children}
        </div>
    );
}

function Hi({children}) {
    return <span className="text-[#ef5630] font-semibold">{children}</span>;
}

function PlayIcon() {
    return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8 5v14l11-7z"/>
        </svg>
    );
}

function PauseIcon() {
    return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="6" y="5" width="4" height="14" rx="1"/>
            <rect x="14" y="5" width="4" height="14" rx="1"/>
        </svg>
    );
}

function CheckIcon() {
    return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20 6L9 17l-5-5"/>
        </svg>
    );
}

function SparkleIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2l1.7 5.6L19 9.3l-5.3 1.7L12 16l-1.7-5L5 9.3l5.3-1.7L12 2z"/>
            <circle cx="19" cy="4" r="1.2"/>
            <circle cx="5.5" cy="18.5" r="1.4"/>
        </svg>
    );
}

